import { useCallback, useEffect, useRef, useState } from 'react';

const query = (px: number) => `(max-width: ${px - 0.02}px)`;

const matches = (px: number): boolean => {
  if (typeof window === 'undefined') return false;
  if (typeof window.matchMedia === 'function') return window.matchMedia(query(px)).matches;
  return window.innerWidth < px; // jsdom senza matchMedia
};

/**
 * Breakpoint master-detail del Centro Ticket. Sta in JS e non in CSS perche
 * governa anche la navigazione (prototipo L437-L440): sotto soglia si mostra
 * una schermata alla volta e il contesto passa nel drawer (ruling 2).
 *
 * Usa `matchMedia` e non `resize`: il browser notifica solo l'attraversamento
 * della soglia, quindi niente handler su ogni pixel di trascinamento.
 */
export const useNarrow = (px: number = 1024): boolean => {
  const [narrow, setNarrow] = useState<boolean>(() => matches(px));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(query(px));
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [px]);
  return narrow;
};

/**
 * Altezza reale della barra titolo sticky, misurata: le 6 pillole KPI vanno a
 * capo intorno ai 1300-1400px di viewport e la barra diventa piu alta dei 76px
 * fissi del prototipo (L556). Il valore alimenta la variabile CSS --s3-bar-h,
 * usata da `top` e `max-height` delle colonne sticky.
 *
 * La misura e sempre border-box (`borderBoxSize`, come
 * `getBoundingClientRect`): usare `contentRect` darebbe un valore piu piccolo
 * di padding+bordi e la barra coprirebbe l'inizio delle colonne.
 */
export const useStickyBarHeight = (): [(el: HTMLDivElement | null) => void, number] => {
  const [h, setH] = useState<number>(56);
  const obs = useRef<ResizeObserver | null>(null);

  const ref = useCallback((el: HTMLDivElement | null) => {
    obs.current?.disconnect();
    if (!el) return;
    setH(el.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') return; // jsdom nei test
    obs.current = new ResizeObserver(([entry]) => {
      setH(entry.borderBoxSize?.[0]?.blockSize ?? el.getBoundingClientRect().height);
    });
    obs.current.observe(el);
  }, []);

  useEffect(() => () => obs.current?.disconnect(), []);
  return [ref, h];
};

export default useNarrow;
